import { useState, useMemo } from "react";
import * as Icons from "lucide-react";
import { BLOCK_TYPES, BLOCK_CATEGORIES } from "@/lib/blockTypes";

function Icon({ name, ...rest }) {
  const key = (name || "").split("-").map((w) => w[0]?.toUpperCase() + w.slice(1)).join("");
  const C = Icons[key] || Icons.Sparkles;
  return <C {...rest} />;
}

/** Modal-style block picker grouped by category. */
export default function BlockPicker({ onPick, onCancel }) {
  const [query, setQuery] = useState("");

  const grouped = useMemo(() => {
    const out = {};
    Object.entries(BLOCK_TYPES).forEach(([type, spec]) => {
      const match = !query
        || spec.label.toLowerCase().includes(query.toLowerCase())
        || type.includes(query.toLowerCase());
      if (!match) return;
      const cat = spec.category || "Other";
      (out[cat] = out[cat] || []).push([type, spec]);
    });
    return out;
  }, [query]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 p-4"
      onClick={onCancel}
      data-testid="block-picker"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[#E5E7EB]">
          <h3 className="font-display text-lg font-semibold text-gray-900">Add a block</h3>
          <input
            data-testid="block-picker-search"
            autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search block types…"
            className="mt-3 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {BLOCK_CATEGORIES.filter((c) => grouped[c]).map((cat) => (
            <div key={cat} className="mb-6 last:mb-0">
              <div className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">{cat}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {grouped[cat].map(([type, spec]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => onPick(type)}
                    data-testid={`block-picker-${type}`}
                    className="text-left p-3 rounded-lg border border-gray-200 hover:border-[#538135] hover:bg-[#EAF4E3]/40 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md bg-[#EAF4E3] text-[#538135] flex items-center justify-center">
                        <Icon name={spec.icon} size={16} strokeWidth={1.75} />
                      </div>
                      <div className="text-sm font-medium text-gray-900">{spec.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {Object.keys(grouped).length === 0 && (
            <div className="text-center py-10 text-sm text-gray-500">No blocks match "{query}".</div>
          )}
        </div>
      </div>
    </div>
  );
}
